import {
  useCallback, useEffect, useId, useLayoutEffect, useRef, useState,
  type ChangeEvent, type CSSProperties, type KeyboardEvent, type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import Ic from "./icons.jsx";
import {
  SR_MONTHS, SR_WEEKDAYS_SHORT, daysInMonth, formatSrDate, formatSrDateTime,
  parseSrDate, parseSrDateTime, srMonthTitle, toIsoDate, toLocalDateTimeInput,
} from "../lib/dates";

/**
 * App-styled replacement for the native `<input type="date">` /
 * `type="datetime-local">` (Strahinja, 2026-09-10: "na celoj app ovaj
 * datepicker mora da bude moderan"). The native control showed English month
 * names, Sunday-first weeks, 12 h AM/PM and "Clear/Today" — none of that is
 * acceptable in a Serbian tablet UI.
 *
 * Value contract is unchanged: `DateInput` speaks "YYYY-MM-DD", `DateTimeInput`
 * "YYYY-MM-DDTHH:mm" (24 h), "" when empty — so callers keep their state and
 * `min`/`max` untouched. The field itself is a text `.input` showing the
 * Serbian form ("10.09.2026", "10.09.2026 18:33") that the user can type into;
 * the calendar icon (or ArrowDown) opens a fixed-position popover portal, the
 * same anchoring/flip pattern as `Select.tsx`. Monday-first, Serbian names,
 * keyboard grid navigation, `role="dialog"`.
 */

export type DateInputProps = {
  /** "YYYY-MM-DD" (or "YYYY-MM-DDTHH:mm" for the date-time variant), "" when empty. */
  value: string;
  onChange: (value: string) => void;
  /** Same format as `value`; earlier days are disabled in the calendar and rejected when typed. */
  min?: string;
  max?: string;
  disabled?: boolean;
  required?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  /** Extra class on the wrapper. */
  className?: string;
  /** Applied to the wrapper — use for width / margins. */
  style?: CSSProperties;
};

type Mode = "date" | "datetime";

type ModeConfig = {
  parse: (text: string) => string | null;
  format: (value: string) => string;
  placeholder: string;
  now: () => string;
};

const MODES: Record<Mode, ModeConfig> = {
  date: { parse: parseSrDate, format: formatSrDate, placeholder: "dd.mm.gggg", now: () => toIsoDate() },
  datetime: { parse: parseSrDateTime, format: formatSrDateTime, placeholder: "dd.mm.gggg hh:mm", now: () => toLocalDateTimeInput() },
};

const PANEL_GAP = 4;
const VIEWPORT_MARGIN = 8;
/** Used before the panel has been measured (and in jsdom). */
const PANEL_FALLBACK_H = 400;
const PANEL_FALLBACK_W = 298;

const pad = (n: number) => String(n).padStart(2, "0");

/** Day part of either value shape; "" stays "". */
const dayOf = (value: string) => value.slice(0, 10);

function inRange(value: string, min?: string, max?: string): boolean {
  return (!min || value >= min) && (!max || value <= max);
}

function clampToRange(value: string, min?: string, max?: string): string {
  if (min && value < min) return min;
  if (max && value > max) return max;
  return value;
}

function ymOf(isoDay: string): { y: number; m: number } {
  return { y: Number(isoDay.slice(0, 4)), m: Number(isoDay.slice(5, 7)) - 1 };
}

function addDays(isoDay: string, days: number): string {
  const { y, m } = ymOf(isoDay);
  return toIsoDate(new Date(y, m, Number(isoDay.slice(8, 10)) + days));
}

/** Same day-of-month `months` later (clamped to the target month's length). */
function addMonths(isoDay: string, months: number): string {
  const { y, m } = ymOf(isoDay);
  const target = new Date(y, m + months, 1);
  const d = Math.min(Number(isoDay.slice(8, 10)), daysInMonth(target.getFullYear(), target.getMonth()));
  return toIsoDate(new Date(target.getFullYear(), target.getMonth(), d));
}

function startOfWeek(isoDay: string): string {
  const { y, m } = ymOf(isoDay);
  const date = new Date(y, m, Number(isoDay.slice(8, 10)));
  return addDays(isoDay, -((date.getDay() + 6) % 7));
}

/** The 6 × 7 grid of a month, Monday first, padded with neighbouring days. */
function monthGrid(y: number, m: number): string[] {
  const offset = (new Date(y, m, 1).getDay() + 6) % 7;
  return Array.from({ length: 42 }, (_, i) => toIsoDate(new Date(y, m, 1 - offset + i)));
}

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");

export function DateInput(props: DateInputProps) {
  return <DateField mode="date" {...props} />;
}

export function DateTimeInput(props: DateInputProps) {
  return <DateField mode="datetime" {...props} />;
}

function DateField({
  mode, value, onChange, min, max, disabled, required, ariaLabel, placeholder, className, style,
}: DateInputProps & { mode: Mode }) {
  const { t } = useTranslation();
  const cfg = MODES[mode];
  const [text, setText] = useState(() => cfg.format(value));
  const [invalid, setInvalid] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogId = useId();

  // An external value change (reset, popover pick, "Danas") refreshes the text
  // unless what the user typed already reads as that value.
  useEffect(() => {
    if (cfg.parse(text) !== value) {
      setText(cfg.format(value));
      setInvalid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function handleText(e: ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setText(next);
    const parsed = cfg.parse(next);
    if (parsed === null || (parsed !== "" && !inRange(parsed, min, max))) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (parsed !== value) onChange(parsed);
  }

  function handleBlur() {
    const parsed = cfg.parse(text);
    if (parsed !== null && (parsed === "" || inRange(parsed, min, max))) setText(cfg.format(parsed));
  }

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) inputRef.current?.focus({ preventScroll: true });
  }, []);

  function onInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (e.key === "ArrowDown" && !open) {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    }
  }

  return (
    <div ref={wrapRef} className={cx("dp-field", className)} style={style}>
      <input
        ref={inputRef}
        type="text"
        className={cx("input", invalid && "is-invalid")}
        value={text}
        onChange={handleText}
        onBlur={handleBlur}
        onKeyDown={onInputKeyDown}
        disabled={disabled}
        required={required}
        placeholder={placeholder ?? cfg.placeholder}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        aria-haspopup="dialog"
        aria-controls={open ? dialogId : undefined}
        autoComplete="off"
        spellCheck={false}
      />
      <button
        type="button"
        className="dp-icon"
        tabIndex={-1}
        disabled={disabled}
        aria-label={t("dp.open", "Otvori kalendar")}
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => (open ? close(true) : setOpen(true))}
      >
        <Ic.calendar width={16} height={16} aria-hidden />
      </button>
      {open && createPortal(
        <DatePickerPopover
          id={dialogId}
          mode={mode}
          value={value}
          min={min}
          max={max}
          anchorRef={wrapRef}
          onChange={onChange}
          onClose={close}
          t={t}
        />,
        document.body,
      )}
    </div>
  );
}

type PopoverProps = {
  id: string;
  mode: Mode;
  value: string;
  min?: string;
  max?: string;
  anchorRef: RefObject<HTMLElement>;
  onChange: (value: string) => void;
  onClose: (restoreFocus: boolean) => void;
  t: (key: string, fallback: string) => string;
};

type Panel = "days" | "months" | "years";

/**
 * The calendar itself: month grid (Monday first), month/year quick pick,
 * optional 24 h time row, "Obriši" / "Danas|Sada" / "U redu" footer. Rendered
 * in a body portal with fixed positioning under the field; flips above when
 * the viewport has no room below. Height is constant across the three panels
 * so switching them never moves the footer (blueprint 09).
 */
function DatePickerPopover({ id, mode, value, min, max, anchorRef, onChange, onClose, t }: PopoverProps) {
  const cfg = MODES[mode];
  const today = toIsoDate();
  const selectedDay = dayOf(value);
  const minDay = min ? dayOf(min) : undefined;
  const maxDay = max ? dayOf(max) : undefined;
  const startDay = selectedDay || clampToRange(today, minDay, maxDay);

  const [view, setView] = useState(() => ymOf(startDay));
  const [focusDay, setFocusDay] = useState(startDay);
  const [panel, setPanel] = useState<Panel>("days");
  const [yearBase, setYearBase] = useState(() => ymOf(startDay).y - 5);
  const [hh, setHh] = useState(() => (value ? value.slice(11, 13) : "00"));
  const [mm, setMm] = useState(() => (value ? value.slice(14, 16) : "00"));
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const [flipped, setFlipped] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Anchor the fixed panel to the field; follow scroll/resize of any ancestor (same as Select).
  useLayoutEffect(() => {
    function place() {
      const anchor = anchorRef.current;
      const el = panelRef.current;
      if (!anchor || !el) return;
      const r = anchor.getBoundingClientRect();
      const h = el.offsetHeight || PANEL_FALLBACK_H;
      const w = el.offsetWidth || PANEL_FALLBACK_W;
      const below = window.innerHeight - r.bottom - PANEL_GAP;
      const above = r.top - PANEL_GAP;
      const flip = below < h && above > below;
      const left = Math.max(VIEWPORT_MARGIN, Math.min(r.left, window.innerWidth - w - VIEWPORT_MARGIN));
      setFlipped(flip);
      setPos(flip ? { left, bottom: window.innerHeight - r.top + PANEL_GAP } : { left, top: r.bottom + PANEL_GAP });
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorRef]);

  // Commit a possibly half-typed time before the popover goes away.
  const commitTime = useCallback((h: string, m: string) => {
    const day = dayOf(value);
    if (!day) return;
    const next = clampToRange(`${day}T${pad(Math.min(23, Number(h || "0")))}:${pad(Math.min(59, Number(m || "0")))}`, min, max);
    if (next !== value) onChange(next);
  }, [value, min, max, onChange]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      if (mode === "datetime") commitTime(hh, mm);
      onClose(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [anchorRef, onClose, mode, commitTime, hh, mm]);

  // The value is the source of truth for the time row; it changes only through our own commits.
  useEffect(() => {
    if (mode === "datetime" && value) {
      setHh(value.slice(11, 13));
      setMm(value.slice(14, 16));
    }
  }, [mode, value]);

  // Roving focus: the focused day cell takes keyboard focus whenever the day grid shows.
  useEffect(() => {
    if (panel !== "days") return;
    const el = panelRef.current;
    const active = document.activeElement;
    // Focus on a nav button or the time row stays there (repeated ‹ › clicks); everything else lands on the grid.
    if (el && active && el.contains(active) && !active.closest(".dp-grid")) return;
    el?.querySelector<HTMLElement>(`[data-day="${focusDay}"]`)?.focus({ preventScroll: true });
  }, [panel, focusDay, view]);

  const isDayDisabled = (day: string) => !inRange(day, minDay, maxDay);

  function moveFocus(day: string) {
    setFocusDay(day);
    setView(ymOf(day));
  }

  function pickDay(day: string) {
    if (isDayDisabled(day)) return;
    setFocusDay(day);
    if (mode === "date") {
      onChange(day);
      onClose(true);
      return;
    }
    onChange(clampToRange(`${day}T${hh}:${mm}`, min, max));
  }

  function pickNow() {
    onChange(clampToRange(cfg.now(), min, max));
    onClose(true);
  }

  function clear() {
    onChange("");
    onClose(true);
  }

  // Escape / Tab close the popover. A native listener on the panel runs before the
  // enclosing Modal's document listener, so stopping propagation keeps the modal open.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key !== "Escape" && e.key !== "Tab") return;
      e.preventDefault();
      e.stopPropagation();
      if (mode === "datetime") commitTime(hh, mm);
      onClose(true);
    }
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [mode, commitTime, hh, mm, onClose]);

  function onGridKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    let next: string;
    switch (e.key) {
      case "ArrowLeft": next = addDays(focusDay, -1); break;
      case "ArrowRight": next = addDays(focusDay, 1); break;
      case "ArrowUp": next = addDays(focusDay, -7); break;
      case "ArrowDown": next = addDays(focusDay, 7); break;
      case "PageUp": next = addMonths(focusDay, e.shiftKey ? -12 : -1); break;
      case "PageDown": next = addMonths(focusDay, e.shiftKey ? 12 : 1); break;
      case "Home": next = startOfWeek(focusDay); break;
      case "End": next = addDays(startOfWeek(focusDay), 6); break;
      case "Enter":
      case " ":
        e.preventDefault();
        pickDay(focusDay);
        return;
      default:
        return;
    }
    e.preventDefault();
    moveFocus(next);
  }

  function shiftView(delta: number) {
    if (panel === "days") {
      const target = new Date(view.y, view.m + delta, 1);
      setView({ y: target.getFullYear(), m: target.getMonth() });
      setFocusDay(addMonths(focusDay, delta));
    } else if (panel === "months") {
      setView((v) => ({ y: v.y + delta, m: v.m }));
    } else {
      setYearBase((b) => b + delta * 12);
    }
  }

  function onTitleClick() {
    if (panel === "days") setPanel("months");
    else if (panel === "months") { setYearBase(view.y - 5); setPanel("years"); }
    else setPanel("days");
  }

  function pickMonth(m: number) {
    const d = Math.min(Number(focusDay.slice(8, 10)), daysInMonth(view.y, m));
    setView({ y: view.y, m });
    setFocusDay(toIsoDate(new Date(view.y, m, d)));
    setPanel("days");
  }

  function pickYear(y: number) {
    setView({ y, m: view.m });
    setPanel("months");
  }

  function onTimeChange(setter: (v: string) => void) {
    return (e: ChangeEvent<HTMLInputElement>) => setter(e.target.value.replace(/\D/g, "").slice(0, 2));
  }

  function onTimeKeyDown(part: "h" | "m") {
    return (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        const dir = e.key === "ArrowUp" ? 1 : -1;
        if (part === "h") {
          const next = pad((Number(hh || "0") + dir * (e.shiftKey ? 10 : 1) + 24 * 10) % 24);
          setHh(next);
          commitTime(next, mm);
        } else {
          const next = pad((Number(mm || "0") + dir * (e.shiftKey ? 15 : 1) + 60 * 15) % 60);
          setMm(next);
          commitTime(hh, next);
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        commitTime(hh, mm);
        onClose(true);
      }
    };
  }

  function onTimeBlur(part: "h" | "m") {
    return () => {
      const h = pad(Math.min(23, Number(hh || "0")));
      const m = pad(Math.min(59, Number(mm || "0")));
      if (part === "h") setHh(h); else setMm(m);
      commitTime(h, m);
    };
  }

  const title = panel === "days"
    ? srMonthTitle(view.y, view.m)
    : panel === "months" ? String(view.y) : `${yearBase} – ${yearBase + 11}`;
  const navLabels = panel === "days"
    ? [t("dp.prevMonth", "Prethodni mesec"), t("dp.nextMonth", "Sledeći mesec")]
    : panel === "months"
      ? [t("dp.prevYear", "Prethodna godina"), t("dp.nextYear", "Sledeća godina")]
      : [t("dp.prevYears", "Prethodnih 12 godina"), t("dp.nextYears", "Sledećih 12 godina")];

  const days = monthGrid(view.y, view.m);
  const weeks = Array.from({ length: 6 }, (_, i) => days.slice(i * 7, i * 7 + 7));

  return (
    <div
      ref={panelRef}
      id={id}
      role="dialog"
      aria-label={t("dp.dialog", "Izbor datuma")}
      className={cx("dp-panel", flipped && "is-up")}
      style={pos ?? { top: 0, left: 0, visibility: "hidden" }}
    >
      <div className="dp-head">
        <button type="button" className="dp-nav" aria-label={navLabels[0]} onClick={() => shiftView(-1)}>
          <Ic.chev width={16} height={16} aria-hidden style={{ transform: "rotate(180deg)" }} />
        </button>
        <button type="button" className="dp-title" aria-label={t("dp.pickMonth", "Izaberi mesec i godinu")} onClick={onTitleClick}>
          {title}
        </button>
        <button type="button" className="dp-nav" aria-label={navLabels[1]} onClick={() => shiftView(1)}>
          <Ic.chev width={16} height={16} aria-hidden />
        </button>
      </div>

      <div className="dp-body">
        {panel === "days" && (
          <div role="grid" aria-label={title} className="dp-grid">
            <div role="row" className="dp-weekdays">
              {SR_WEEKDAYS_SHORT.map((w) => <span key={w} role="columnheader">{w}</span>)}
            </div>
            {weeks.map((week) => (
              <div key={week[0]} role="row" className="dp-week">
                {week.map((day) => {
                  const off = isDayDisabled(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      role="gridcell"
                      data-day={day}
                      tabIndex={day === focusDay ? 0 : -1}
                      aria-selected={day === selectedDay}
                      aria-disabled={off || undefined}
                      aria-label={formatSrDate(day)}
                      className={cx(
                        "dp-day",
                        !day.startsWith(`${view.y}-${pad(view.m + 1)}`) && "is-outside",
                        day === today && "is-today",
                        day === selectedDay && "is-selected",
                        off && "is-disabled",
                      )}
                      onClick={() => pickDay(day)}
                      onKeyDown={onGridKeyDown}
                    >
                      {Number(day.slice(8, 10))}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {panel === "months" && (
          <div className="dp-cells">
            {SR_MONTHS.map((name, m) => (
              <button
                key={name}
                type="button"
                className={cx("dp-cell", m === view.m && "is-selected")}
                onClick={() => pickMonth(m)}
              >
                {name.charAt(0).toUpperCase() + name.slice(1)}
              </button>
            ))}
          </div>
        )}
        {panel === "years" && (
          <div className="dp-cells">
            {Array.from({ length: 12 }, (_, i) => yearBase + i).map((y) => (
              <button
                key={y}
                type="button"
                className={cx("dp-cell", y === view.y && "is-selected")}
                onClick={() => pickYear(y)}
              >
                {y}
              </button>
            ))}
          </div>
        )}
      </div>

      {mode === "datetime" && (
        <div className="dp-time">
          <Ic.clock width={16} height={16} aria-hidden />
          <input
            type="text"
            inputMode="numeric"
            className="input"
            value={hh}
            aria-label={t("dp.hours", "Sati")}
            onChange={onTimeChange(setHh)}
            onKeyDown={onTimeKeyDown("h")}
            onBlur={onTimeBlur("h")}
            maxLength={2}
          />
          <span className="dp-colon">:</span>
          <input
            type="text"
            inputMode="numeric"
            className="input"
            value={mm}
            aria-label={t("dp.minutes", "Minuti")}
            onChange={onTimeChange(setMm)}
            onKeyDown={onTimeKeyDown("m")}
            onBlur={onTimeBlur("m")}
            maxLength={2}
          />
        </div>
      )}

      <div className="dp-foot">
        <button type="button" className="btn" onClick={clear}>{t("dp.clear", "Obriši")}</button>
        <span className="dp-spacer" />
        <button type="button" className="btn" onClick={pickNow}>
          {mode === "date" ? t("dp.today", "Danas") : t("dp.now", "Sada")}
        </button>
        {mode === "datetime" && (
          <button type="button" className="btn primary" onClick={() => { commitTime(hh, mm); onClose(true); }}>
            {t("dp.ok", "U redu")}
          </button>
        )}
      </div>
    </div>
  );
}
